import React, { useMemo } from "react";
import { Chip, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import { useQuery } from "@tanstack/react-query";

import { fetchSuperInstitutos } from "../../../../shared/fetchers/classics/institutos/verSuperInstitutos";
import { fetchInstitutos } from "../../../../shared/fetchers/classics/institutos/verInstitutos";
import { fetchClientesPorSuperInstituto } from "../../../../shared/fetchers/clientes";

/**
 * Cascada SuperInstituto -> institutos -> clientes.
 *
 * Del alcance más específico sale el territorio que se le exige a los libros, así
 * que se muestra el resultado para que el usuario sepa qué países se van a pedir.
 */
const AlcanceSelector = ({ alcance, onChange }) => {
  const { data: superInstitutos = [] } = useQuery(
    ["superInstitutos"],
    fetchSuperInstitutos
  );
  const { data: institutos = [] } = useQuery(["institutos"], fetchInstitutos);
  const { data: clientes = [] } = useQuery(
    ["clientes-super-instituto", alcance.superInstituto],
    () => fetchClientesPorSuperInstituto(alcance.superInstituto),
    { enabled: Boolean(alcance.superInstituto) }
  );

  const institutosDelSuper = useMemo(
    () =>
      institutos.filter(
        (instituto) => instituto?.superInstituto === alcance.superInstituto
      ),
    [institutos, alcance.superInstituto]
  );

  const clientesDisponibles = useMemo(() => {
    if (alcance.institutos.length === 0) return clientes;
    return clientes.filter((cliente) =>
      alcance.institutos.includes(cliente?.institute)
    );
  }, [clientes, alcance.institutos]);

  // Mismo criterio que aplica el backend: se toma el nivel más específico elegido.
  const paises = useMemo(() => {
    let institutosBase = institutosDelSuper;

    if (alcance.clientes.length > 0) {
      const institutosDeClientes = clientes
        .filter((cliente) => alcance.clientes.includes(cliente?.username))
        .map((cliente) => cliente?.institute);
      institutosBase = institutosDelSuper.filter((instituto) =>
        institutosDeClientes.includes(instituto?.nombre)
      );
    } else if (alcance.institutos.length > 0) {
      institutosBase = institutosDelSuper.filter((instituto) =>
        alcance.institutos.includes(instituto?.nombre)
      );
    }

    return [
      ...new Set(
        institutosBase
          .map((instituto) => instituto?.pais)
          .filter((pais) => typeof pais === "string" && pais.length > 0)
      ),
    ];
  }, [institutosDelSuper, clientes, alcance.clientes, alcance.institutos]);

  const superInstitutoSeleccionado =
    superInstitutos.find((si) => si?.nombre === alcance.superInstituto) ?? null;

  const handleSuperInstituto = (valor) => {
    // Institutos y clientes dependen del SuperInstituto, así que se reinician.
    onChange({
      superInstituto: valor?.nombre ?? "",
      institutos: [],
      clientes: [],
    });
  };

  return (
    <div>
      <div className="d-flex align-items-center">
        <AccountBalanceIcon style={{ fontSize: "17px" }} />
        <div className="admin-main-title-dark-gray ml-2 mt-1">Alcance</div>
      </div>

      <label className="admin-label-text mt-3 d-block">Super Instituto:</label>
      <Autocomplete
        options={superInstitutos}
        getOptionLabel={(option) => option?.nombre ?? ""}
        getOptionSelected={(option, valor) => option?.nombre === valor?.nombre}
        value={superInstitutoSeleccionado}
        onChange={(_, valor) => handleSuperInstituto(valor)}
        noOptionsText="Sin resultados"
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder="Selecciona un super instituto"
            style={{ width: "100%", fontFamily: "'Noto Sans JP', sans-serif" }}
          />
        )}
      />

      <label className="admin-label-text mt-3 d-block">
        Institutos (opcional, por defecto todos):
      </label>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={institutosDelSuper.map((instituto) => instituto.nombre)}
        getOptionLabel={(option) => option}
        value={alcance.institutos}
        onChange={(_, valor) =>
          onChange({ ...alcance, institutos: valor, clientes: [] })
        }
        disabled={!alcance.superInstituto}
        noOptionsText="Sin resultados"
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder="Todos los institutos"
            style={{ width: "100%", fontFamily: "'Noto Sans JP', sans-serif" }}
          />
        )}
      />

      <label className="admin-label-text mt-3 d-block">
        Clientes específicos (opcional):
      </label>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={clientesDisponibles.map((cliente) => cliente.username)}
        getOptionLabel={(option) => {
          const cliente = clientesDisponibles.find(
            (c) => c.username === option
          );
          return cliente?.nombreCliente
            ? `${cliente.nombreCliente} (${option})`
            : option;
        }}
        value={alcance.clientes}
        onChange={(_, valor) => onChange({ ...alcance, clientes: valor })}
        disabled={!alcance.superInstituto}
        noOptionsText="Sin resultados"
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder="Todos los clientes"
            style={{ width: "100%", fontFamily: "'Noto Sans JP', sans-serif" }}
          />
        )}
      />

      <div className="admin-muted-text-small mt-3">
        Territorio exigido a los libros:{" "}
        {paises.length > 0 ? (
          paises.map((pais) => (
            <Chip key={pais} size="small" label={pais} className="ml-1" />
          ))
        ) : (
          <span className="text-danger">
            sin determinar, revisa el país de los institutos
          </span>
        )}
      </div>
    </div>
  );
};

export default AlcanceSelector;
